import React, { useEffect, useRef } from 'react'
import { t } from 'src/translations/src'
import SavingsTooltip from './SavingsTooltip'
import { useFormatPrice } from 'src/lib/js/hooks/useFormatPrice'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { SavingsBreakdown } from './types'

type PostOrderScreenProps = {
  discountTotal?: number
  savingsBreakdown?: SavingsBreakdown
  pointsEarned?: number | null
  pointsEarnedMessage?: string | null
  customerEmail?: string
  restaurantGuid?: string
  onDismiss?: () => void
}

const PostOrderScreen = ({
  discountTotal,
  savingsBreakdown,
  pointsEarned,
  pointsEarnedMessage,
  customerEmail,
  restaurantGuid,
  onDismiss
}: PostOrderScreenProps) => {
  const hasDiscounts = !!discountTotal
  const hasPointsEarned = !!pointsEarned && pointsEarned > 0
  const pointsMessage =
    pointsEarnedMessage ??
    (pointsEarned
      ? `${pointsEarned} ${t('order-confirmation.post-order.points')}`
      : null)
  const tracker = useTracker()
  const hasTrackedOrderSuccessScreenRef = useRef(false)

  // Primitives for effect deps — parent often passes a new savingsBreakdown object each render.
  const promoCodeAmount = savingsBreakdown?.promoCodeAmount ?? 0
  const loyaltyRewardAmount = savingsBreakdown?.loyaltyRewardAmount ?? 0
  const deliverySavingsAmount = savingsBreakdown?.deliverySavingsAmount ?? 0

  // Format currency using existing hook
  const formattedDiscountTotal = useFormatPrice(discountTotal ?? 0, true)

  // Track Order Success Screen Viewed
  useEffect(() => {
    if (hasTrackedOrderSuccessScreenRef.current) {
      return
    }

    const experience: string[] = []

    if (promoCodeAmount > 0) {
      experience.push('promo-code-savings')
    }
    if (loyaltyRewardAmount > 0) {
      experience.push('loyalty-savings')
    }
    if (deliverySavingsAmount > 0) {
      experience.push('delivery-savings')
    }
    if (hasPointsEarned) {
      experience.push('loyalty-points-earned')
    }

    // User sees goodwill message if they have no savings AND no points earned
    if (experience.length === 0) {
      experience.push('goodwill-message')
    }

    tracker.track(TrackingEvents.ORDER_SUCCESS_SCREEN_VIEWED, {
      restaurantGuid,
      experience
    })
    hasTrackedOrderSuccessScreenRef.current = true
  }, [
    tracker,
    restaurantGuid,
    promoCodeAmount,
    loyaltyRewardAmount,
    deliverySavingsAmount,
    hasPointsEarned
  ])

  return (
    <div className='postOrderScreen'>
      <div className='animationContainer' data-testid='animation-container'>
        <PaperBagAnimationDrop />
      </div>

      <div className='orderStatusContainer'>
        <h4>{t('order-confirmation.order-status.placed')}</h4>
        <div>
          {t('order-confirmation.confirmation-sent', {
            email: customerEmail ?? ''
          })}
        </div>
      </div>

      <div className='contentSection'>
        <div className='messageCard'>
          {/* Background Icons Layer */}
          {(hasDiscounts || hasPointsEarned) && (
            <div className='backgroundIcons'>
              <div className='priceTag'>
                <PriceTag />
              </div>
              <div className='percentageSticker'>
                <PercentageSticker />
              </div>
            </div>
          )}

          {/* Text Content Layer - above icons */}
          <div className='textContent'>
            {hasDiscounts && !hasPointsEarned && (
              <div className='singleReward'>
                <h4>{formattedDiscountTotal}</h4>
                <div>
                  {t('order-confirmation.post-order.saved-on-order')}
                  {savingsBreakdown && discountTotal > 0 && (
                    <SavingsTooltip
                      breakdown={savingsBreakdown}
                      totalSaved={discountTotal}
                    />
                  )}
                </div>
              </div>
            )}
            {hasPointsEarned && !hasDiscounts && (
              <div className='singleReward'>
                <h4>{pointsMessage}</h4>
                <div>{t('order-confirmation.post-order.earned-on-order')}</div>
              </div>
            )}
            {hasDiscounts && hasPointsEarned && (
              <div className='comboReward'>
                <div id='savings'>
                  <h5>{formattedDiscountTotal}</h5>
                  <div>
                    {t('order-confirmation.post-order.total-saved')}
                    {savingsBreakdown && discountTotal > 0 && (
                      <SavingsTooltip
                        breakdown={savingsBreakdown}
                        totalSaved={discountTotal}
                      />
                    )}
                  </div>
                </div>
                <div id='loyalty'>
                  <h5>{pointsMessage}</h5>
                  <div>{t('order-confirmation.post-order.earned')}</div>
                </div>
              </div>
            )}
            {!hasDiscounts && !hasPointsEarned && (
              <div className='singleReward thankYou'>
                <h5>{t('order-confirmation.post-order.thank-you-direct')}</h5>
                <p>{t('order-confirmation.post-order.thank-you-subtitle')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='ctaSection'>
        <button type='button' onClick={onDismiss}>
          {t('order-confirmation.post-order.view-order-status')}
        </button>
      </div>
    </div>
  )
}

const PriceTag = () => (
  <svg
    width='43'
    height='55'
    viewBox='0 0 43 55'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <g clipPath='url(#clip0_5462_21403)'>
      <g filter='url(#filter0_dddd_5462_21403)'>
        <path
          d='M13.9097 13.0659C14.4293 12.0826 15.3498 11.3731 16.4329 11.121L27.1313 8.63105C28.9925 8.19786 30.8889 9.19999 31.5796 10.9818L35.5501 21.2234C35.9521 22.2602 35.8845 23.4205 35.365 24.4037L23.9807 45.9468C22.9904 47.8209 20.6684 48.5372 18.7943 47.5469L4.12551 39.7953C2.25148 38.805 1.5351 36.483 2.52541 34.609L13.9097 13.0659Z'
          fill='#006C61'
        />
      </g>
      <g filter='url(#filter1_ddddd_5462_21403)'>
        <path
          d='M13.9645 5.30058C14.8789 4.66757 16.0228 4.46228 17.1002 4.73788L28.5337 7.66256C30.3851 8.13614 31.606 9.89963 31.3976 11.7992L30.1107 23.5305C29.9894 24.6359 29.3946 25.6344 28.4803 26.2674L7.19386 41.0044C5.45115 42.2109 3.06034 41.7763 1.85383 40.0335L-8.29276 25.3776C-9.49927 23.6349 -9.0646 21.2441 -7.32189 20.0376L13.9645 5.30058Z'
          fill='#46AD84'
        />
      </g>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M21.0006 16.7549C21.448 17.3959 21.2911 18.2782 20.6501 18.7256L19.9274 19.2301C20.2865 20.0854 20.4329 21.0228 20.3436 21.9605L20.3419 21.9781C20.2887 22.4709 20.1607 22.9526 19.9625 23.4068C19.6498 24.1232 18.8156 24.4505 18.0992 24.1379C17.3827 23.8252 17.0554 22.991 17.3681 22.2746C17.4502 22.0864 17.5036 21.8869 17.5265 21.683C17.591 20.9818 17.3922 20.2816 16.9687 19.7188C16.9484 19.6918 16.9291 19.6641 16.9108 19.6358C16.5239 19.037 15.958 18.5757 15.2935 18.3174L15.2914 18.3166C15.0762 18.2325 14.8433 18.2041 14.6142 18.2338C14.3851 18.2635 14.1672 18.3505 13.9807 18.4867C13.9538 18.5064 13.9262 18.525 13.898 18.5427C13.7196 18.6546 13.5715 18.8087 13.4668 18.9913C13.368 19.1639 13.3107 19.3569 13.2993 19.5551C13.328 20.387 13.504 21.2073 13.8191 21.978C13.83 22.0046 13.8401 22.0316 13.8493 22.0589L13.8576 22.0833C14.3045 23.1891 14.5417 24.3685 14.557 25.5613C14.5573 25.5811 14.5571 25.601 14.5566 25.6208C14.5364 26.3127 14.3471 26.9891 14.0053 27.591C13.6717 28.1786 13.2027 28.6776 12.6375 29.047C12.0625 29.4623 11.3932 29.7286 10.6896 29.8217C9.97041 29.9168 9.23889 29.8278 8.5635 29.5631C7.88484 29.2993 7.26131 28.9216 6.71774 28.4502L6.01622 28.9398C5.37524 29.3872 4.49295 29.2303 4.04558 28.5893C3.5982 27.9483 3.75514 27.066 4.39612 26.6187L5.00162 26.196C4.57718 25.3468 4.33078 24.4151 4.28286 23.4586L4.28237 23.4487C4.25598 22.8365 4.35839 22.2256 4.58291 21.6554C4.86931 20.9281 5.69109 20.5707 6.41839 20.8571C7.1457 21.1435 7.50312 21.9653 7.21672 22.6926C7.13788 22.8928 7.10162 23.1072 7.11025 23.3222C7.15095 24.1141 7.42241 24.8768 7.89125 25.5164C7.90682 25.5376 7.92179 25.5593 7.93615 25.5814C8.33317 26.1917 8.91192 26.6618 9.59062 26.9254L9.5955 26.9273C9.82512 27.0175 10.0739 27.0478 10.3184 27.0154C10.563 26.9831 10.7953 26.8891 10.9936 26.7424C11.0201 26.7228 11.0473 26.7041 11.0752 26.6863C11.2693 26.5627 11.4302 26.3934 11.5438 26.1933C11.6532 26.0007 11.7156 25.7852 11.7261 25.5642C11.7111 24.7234 11.5399 23.8926 11.2211 23.1142C11.2101 23.0873 11.1999 23.0601 11.1905 23.0326L11.1821 23.0078C10.7428 21.9193 10.501 20.7611 10.4683 19.5875C10.4676 19.5603 10.4676 19.5331 10.4684 19.5059C10.4885 18.8305 10.6748 18.1704 11.0107 17.5841C11.3383 17.0123 11.7985 16.5278 12.3521 16.1713C12.9138 15.7713 13.5655 15.5155 14.2498 15.4267C14.9501 15.3358 15.6621 15.4227 16.32 15.6794C17.0551 15.9653 17.7245 16.3865 18.2958 16.9168L19.0299 16.4044C19.6709 15.957 20.5532 16.1139 21.0006 16.7549Z'
        fill='white'
      />
    </g>
    <defs>
      <filter
        id='filter0_dddd_5462_21403'
        x='-0.499137'
        y='8.53052'
        width='38.8879'
        height='45.0276'
        filterUnits='userSpaceOnUse'
        colorInterpolationFilters='sRGB'
      >
        <feFlood floodOpacity='0' result='BackgroundImageFix' />
        <feColorMatrix
          in='SourceAlpha'
          type='matrix'
          values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          result='hardAlpha'
        />
        <feOffset dy='0.475399' />
        <feGaussianBlur stdDeviation='0.205286' />
        <feColorMatrix
          type='matrix'
          values='0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0.0239667 0'
        />
        <feBlend
          mode='normal'
          in2='BackgroundImageFix'
          result='effect1_dropShadow_5462_21403'
        />
        <feColorMatrix
          in='SourceAlpha'
          type='matrix'
          values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          result='hardAlpha'
        />
        <feOffset dy='1.08014' />
        <feGaussianBlur stdDeviation='0.466426' />
        <feColorMatrix
          type='matrix'
          values='0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0.0348411 0'
        />
        <feBlend
          mode='normal'
          in2='effect1_dropShadow_5462_21403'
          result='effect2_dropShadow_5462_21403'
        />
        <feColorMatrix
          in='SourceAlpha'
          type='matrix'
          values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          result='hardAlpha'
        />
        <feOffset dy='1.87994' />
        <feGaussianBlur stdDeviation='0.811793' />
        <feColorMatrix
          type='matrix'
          values='0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0.0429105 0'
        />
        <feBlend
          mode='normal'
          in2='effect2_dropShadow_5462_21403'
          result='effect3_dropShadow_5462_21403'
        />
        <feColorMatrix
          in='SourceAlpha'
          type='matrix'
          values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          result='hardAlpha'
        />
        <feOffset dy='2.98646' />
        <feGaussianBlur stdDeviation='1.28961' />
        <feColorMatrix
          type='matrix'
          values='0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0.05 0'
        />
        <feBlend
          mode='normal'
          in2='effect3_dropShadow_5462_21403'
          result='effect4_dropShadow_5462_21403'
        />
        <feBlend
          mode='normal'
          in='SourceGraphic'
          in2='effect4_dropShadow_5462_21403'
          result='shape'
        />
      </filter>
      <filter
        id='filter1_ddddd_5462_21403'
        x='-12.6236'
        y='4.61816'
        width='47.6925'
        height='44.9411'
        filterUnits='userSpaceOnUse'
        colorInterpolationFilters='sRGB'
      >
        <feFlood floodOpacity='0' result='BackgroundImageFix' />
        <feColorMatrix
          in='SourceAlpha'
          type='matrix'
          values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          result='hardAlpha'
        />
        <feOffset dy='0.435795' />
        <feGaussianBlur stdDeviation='0.188184' />
        <feColorMatrix
          type='matrix'
          values='0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0.0239667 0'
        />
        <feBlend
          mode='normal'
          in2='BackgroundImageFix'
          result='effect1_dropShadow_5462_21403'
        />
        <feColorMatrix
          in='SourceAlpha'
          type='matrix'
          values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          result='hardAlpha'
        />
        <feOffset dy='0.99016' />
        <feGaussianBlur stdDeviation='0.427569' />
        <feColorMatrix
          type='matrix'
          values='0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0.0348411 0'
        />
        <feBlend
          mode='normal'
          in2='effect1_dropShadow_5462_21403'
          result='effect2_dropShadow_5462_21403'
        />
        <feColorMatrix
          in='SourceAlpha'
          type='matrix'
          values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          result='hardAlpha'
        />
        <feOffset dy='1.72333' />
        <feGaussianBlur stdDeviation='0.744165' />
        <feColorMatrix
          type='matrix'
          values='0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0.0429105 0'
        />
        <feBlend
          mode='normal'
          in2='effect2_dropShadow_5462_21403'
          result='effect3_dropShadow_5462_21403'
        />
        <feColorMatrix
          in='SourceAlpha'
          type='matrix'
          values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          result='hardAlpha'
        />
        <feOffset dy='2.73767' />
        <feGaussianBlur stdDeviation='1.18217' />
        <feColorMatrix
          type='matrix'
          values='0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0.05 0'
        />
        <feBlend
          mode='normal'
          in2='effect3_dropShadow_5462_21403'
          result='effect4_dropShadow_5462_21403'
        />
        <feColorMatrix
          in='SourceAlpha'
          type='matrix'
          values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          result='hardAlpha'
        />
        <feOffset dy='4.22402' />
        <feGaussianBlur stdDeviation='1.82401' />
        <feColorMatrix
          type='matrix'
          values='0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0 0.145098 0 0 0 0.0570895 0'
        />
        <feBlend
          mode='normal'
          in2='effect4_dropShadow_5462_21403'
          result='effect5_dropShadow_5462_21403'
        />
        <feBlend
          mode='normal'
          in='SourceGraphic'
          in2='effect5_dropShadow_5462_21403'
          result='shape'
        />
      </filter>
      <clipPath id='clip0_5462_21403'>
        <rect
          width='47'
          height='47'
          fill='white'
          transform='translate(-2.72656 -4) rotate(16.401)'
        />
      </clipPath>
    </defs>
  </svg>
)

const PercentageSticker = () => (
  <svg
    width='31'
    height='33'
    viewBox='0 0 31 33'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <g clipPath='url(#clip0_5934_8003)'>
      <path
        d='M19.7588 7.94994C21.0687 8.67644 22.7092 8.38993 23.694 7.26035C25.1123 5.63536 27.7025 5.86498 28.8118 7.71463C29.5837 8.99957 31.1465 9.5704 32.5649 9.08544C34.6056 8.3872 36.7341 9.88104 36.7707 12.0365C36.7967 13.5344 37.8653 14.8113 39.3351 15.1001C41.4517 15.5158 42.5475 17.8728 41.5013 19.7591C40.7748 21.069 41.0613 22.7095 42.1909 23.6943C43.8158 25.1126 43.5862 27.7028 41.7366 28.8121C40.4516 29.584 39.8808 31.1469 40.3658 32.5652C41.064 34.6059 39.5702 36.7344 37.4147 36.771C35.9168 36.7971 34.6399 37.8656 34.3511 39.3354C33.9354 41.452 31.5784 42.5478 29.6921 41.5016C28.3822 40.7751 26.7417 41.0616 25.7569 42.1912C24.3386 43.8162 21.7484 43.5865 20.6391 41.7369C19.8672 40.452 18.3043 39.8811 16.886 40.3661C14.8453 41.0643 12.7168 39.5705 12.6802 37.415C12.6541 35.9171 11.5856 34.6402 10.1158 34.3514C7.99922 33.9357 6.90335 31.5787 7.94962 29.6924C8.67612 28.3825 8.38961 26.742 7.26003 25.7572C5.63504 24.3389 5.86466 21.7487 7.71431 20.6394C8.99925 19.8675 9.57008 18.3047 9.08512 16.8863C8.38688 14.8456 9.88072 12.7171 12.0362 12.6805C13.5341 12.6545 14.811 11.5859 15.0998 10.1161C15.5155 7.99954 17.8725 6.90368 19.7588 7.94994Z'
        fill='#006C61'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M26.6941 12.9576C26.9548 12.9912 27.1388 13.2297 27.1053 13.4903L24.2516 35.6371C24.218 35.8978 23.9795 36.0819 23.7188 36.0483C23.4582 36.0147 23.2741 35.7762 23.3077 35.5155L26.1614 13.3687C26.1949 13.1081 26.4335 12.924 26.6941 12.9576Z'
        fill='#E8F7D4'
        stroke='#E8F7D4'
        strokeWidth='0.465334'
        strokeMiterlimit='10'
        strokeLinecap='round'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M29.4893 28.3901C28.5414 28.8308 28.1303 29.9565 28.571 30.9043C29.0117 31.8522 30.1373 32.2633 31.0852 31.8226C32.033 31.3819 32.4442 30.2563 32.0035 29.3084C31.5628 28.3606 30.4371 27.9494 29.4893 28.3901ZM27.708 31.3055C27.0457 29.8811 27.6635 28.1894 29.088 27.5271C30.5125 26.8648 32.2041 27.4827 32.8664 28.9072C33.5287 30.3316 32.9109 32.0233 31.4864 32.6856C30.0619 33.3479 28.3703 32.73 27.708 31.3055Z'
        fill='#E8F7D4'
        stroke='#E8F7D4'
        strokeWidth='0.465334'
        strokeMiterlimit='10'
        strokeLinecap='round'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M19.3257 17.1823C18.3778 17.623 17.9667 18.7487 18.4074 19.6966C18.8481 20.6444 19.9737 21.0555 20.9216 20.6149C21.8694 20.1742 22.2806 19.0485 21.8399 18.1006C21.3992 17.1528 20.2735 16.7417 19.3257 17.1823ZM17.5444 20.0978C16.8821 18.6733 17.5 16.9817 18.9244 16.3194C20.3489 15.6571 22.0406 16.2749 22.7029 17.6994C23.3652 19.1239 22.7473 20.8155 21.3228 21.4778C19.8983 22.1401 18.2067 21.5223 17.5444 20.0978Z'
        fill='#E8F7D4'
        stroke='#E8F7D4'
        strokeWidth='0.465334'
        strokeMiterlimit='10'
        strokeLinecap='round'
      />
    </g>
    <defs>
      <clipPath id='clip0_5934_8003'>
        <rect
          width='37.2267'
          height='37.2267'
          fill='white'
          transform='translate(0 15.6948) rotate(-24.9356)'
        />
      </clipPath>
    </defs>
  </svg>
)

const STAR_PATH =
  'M5.20622 0.0180069C5.1992 -0.00596255 5.16525 -0.00596231 5.15823 0.0180071L3.85558 4.46843C3.85342 4.47581 3.84798 4.48179 3.84084 4.48463L0.0155221 6.00865C-0.00547421 6.01701 -0.00547431 6.04673 0.015522 6.05509L3.84084 7.57911C3.84798 7.58195 3.85342 7.58793 3.85558 7.59531L5.15823 12.0457C5.16525 12.0697 5.19921 12.0697 5.20622 12.0457L6.50887 7.59531C6.51103 7.58793 6.51647 7.58195 6.52361 7.57911L10.3489 6.05509C10.3699 6.04673 10.3699 6.01701 10.3489 6.00864L6.52361 4.48463C6.51647 4.48179 6.51103 4.47581 6.50887 4.46843L5.20622 0.0180069Z'

const PaperBagAnimationDrop = () => {
  return (
    <div className='drop-animation-container'>
      <div className='drop-paper-bag-wrapper'>
        {/* Ground shadow - grows as bag drops, shrinks on impact */}
        <div className='drop-ground-shadow' aria-hidden />

        {/* Poof / impact lines - appear on landing */}
        <div className='drop-poof' aria-hidden>
          <span className='drop-poof-line drop-poof-1' />
          <span className='drop-poof-line drop-poof-2' />
          <span className='drop-poof-line drop-poof-3' />
          <span className='drop-poof-line drop-poof-4' />
          <span className='drop-poof-line drop-poof-5' />
          <span className='drop-poof-line drop-poof-6' />
          <span className='drop-poof-line drop-poof-7' />
          <span className='drop-poof-line drop-poof-8' />
        </div>

        {/* Paper Bag - drops from top, bounces, tilts (orange only; sticker adds the label) */}
        <svg
          className='drop-paper-bag'
          data-testid='paper-bag-animation'
          width='108'
          height='95'
          viewBox='0 0 108 95'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <g clipPath='url(#clip0_drop)'>
            <path
              d='M17.279 0.265381L3.64266 21.7067C2.45714 23.5707 3.7962 26.0093 6.00531 26.0093H30.5886L30.8021 0.265381H17.279Z'
              fill='#F28900'
            />
            <path
              d='M107.22 91.0873C107.535 92.8075 106.214 94.3929 104.466 94.3929H34.5545L17.2798 0.265381H90.5464L107.22 91.0873Z'
              fill='#FF9400'
            />
            <path
              d='M3.36082 94.3929C1.61195 94.3929 0.291058 92.8075 0.606841 91.0874L17.28 0.265381L34.5547 94.3929H3.36082Z'
              fill='#FF4C00'
            />
          </g>
          <defs>
            <clipPath id='clip0_drop'>
              <rect width='107.8' height='94.4584' fill='white' />
            </clipPath>
          </defs>
        </svg>

        {/* Sticker - white label + line, fades in after bag lands */}
        <svg
          className='drop-sticker-label'
          width='108'
          height='95'
          viewBox='0 0 108 95'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M76.2453 55.8668H51.6754L44.8179 18.457H69.3877L76.2453 55.8668Z'
            fill='white'
          />
          <path
            d='M63.6926 23.8254H52.8966C52.571 23.8254 52.3042 23.5585 52.3042 23.233C52.3042 22.9075 52.571 22.6406 52.8966 22.6406H63.6926C64.0181 22.6406 64.2849 22.9075 64.2849 23.233C64.2849 23.5585 64.0181 23.8254 63.6926 23.8254Z'
            fill='#333333'
          />
        </svg>

        {/* Signature squiggle - draws in on top of sticker */}
        <svg
          className='drop-signature'
          width='108'
          height='95'
          viewBox='0 0 108 95'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        >
          <path
            className='drop-signature-path'
            pathLength='1'
            d='M56 47 Q54 50 56 47 Q57 44 56 41 Q55 45 54 47 Q58 44 60 47 T64 47 Q66 44 68 47 Q69 45 70 43'
            stroke='#333333'
            strokeWidth='1.2'
            fill='none'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>

        {/* Stars - appear after the signature */}
        <div className='drop-star-container'>
          <svg
            className='drop-star drop-star-left'
            width='24'
            height='28'
            viewBox='0 0 11 13'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path d={STAR_PATH} fill='#FFB700' />
          </svg>
          <svg
            className='drop-star drop-star-right'
            width='24'
            height='28'
            viewBox='0 0 11 13'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path d={STAR_PATH} fill='#FFB700' />
          </svg>
          <svg
            className='drop-star drop-star-top'
            width='24'
            height='28'
            viewBox='0 0 11 13'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path d={STAR_PATH} fill='#FFB700' />
          </svg>
        </div>
      </div>
    </div>
  )
}

export default PostOrderScreen
